import { useCallback, useRef, useState } from 'react';
import { MultiPageImageModel, MultiPageVideoModel, TextElement, TextRenderer } from '@bynder-studio/render-core';
import { Variation } from './types';

type SetUpdater = Set<string> | ((prev: Set<string>) => Set<string>);

const isSameSet = (a: Set<string>, b: Set<string>) => a.size === b.size && [...a].every((id) => b.has(id));

export const useOversetValidation = ({
    creativeModel,
}: {
    creativeModel?: MultiPageVideoModel | MultiPageImageModel;
}) => {
    const oversetVariationsRef = useRef(new Set<string>());
    const [oversetVariationsSet, setOversetVariationsState] = useState(oversetVariationsRef.current);

    const setOversetVariationsSet = useCallback((updater: SetUpdater) => {
        const prev = oversetVariationsRef.current;
        const next = typeof updater === 'function' ? updater(prev) : updater;

        if (isSameSet(prev, next)) {
            return;
        }

        oversetVariationsRef.current = next;
        setOversetVariationsState(next);
    }, []);

    const updateVariationOverset = useCallback(
        ({ isValid, currentVariation }: { isValid: boolean; currentVariation: string }) => {
            if (!currentVariation) {
                return;
            }

            setOversetVariationsSet((prev) => {
                const temp = new Set<string>(prev);

                if (isValid) {
                    temp.delete(currentVariation);
                } else {
                    temp.add(currentVariation);
                }

                return temp;
            });
        },
        [setOversetVariationsSet],
    );

    const validateVariation = useCallback(
        (variation: Variation, textRenderer = TextRenderer.getInstance()) => {
            return creativeModel.getModelsMetaData()?.some(({ id: pageId }, pageIndex) => {
                const elements = variation?.sizes?.[pageId]?.elements;
                const pageModel = creativeModel.getModels()[pageIndex];

                if (!elements || !pageModel) {
                    return false;
                }

                return Object.entries(elements).some(([elId, value]) => {
                    const { formattedText } = value as any;

                    if (!formattedText) {
                        return false;
                    }

                    const element = pageModel.getElementById(Number(elId)) as TextElement;

                    if (!element || !element.limitTextToBounds) {
                        return false;
                    }

                    const {
                        dimension: { width, height },
                        contentTransform,
                        textControl,
                        minFontScale,
                        fontScale,
                    } = element;
                    const config = {
                        boxWidth: width,
                        boxHeight: height,
                        placement: textControl,
                        horizontalAlignment: contentTransform?.horizontalAlignment,
                        verticalAlignment: contentTransform?.verticalAlignment,
                        textStruct: formattedText,
                        minFontScale,
                        fontScale,
                    };

                    const boundedText = textRenderer.createBoundedText(formattedText, config);

                    return (
                        boundedText.getShapedText().getContentHeight() > height ||
                        boundedText.getShapedText().getContentWidth() > width
                    );
                });
            });
        },
        [creativeModel],
    );

    const updateAllVariationsOverset = useCallback(
        (variations: Variation[]) => {
            const textRenderer = TextRenderer.getInstance();
            const tempOversetVariations = new Set<string>();

            variations.forEach((variation) => {
                const hasOversetText = validateVariation(variation, textRenderer);

                if (hasOversetText) {
                    tempOversetVariations.add(variation.variationSetId);
                }
            });

            setOversetVariationsSet(tempOversetVariations);
        },
        [validateVariation, setOversetVariationsSet],
    );

    return {
        oversetVariationsSet,
        oversetVariationsRef,
        setOversetVariationsSet,
        validateVariation,
        updateVariationOverset,
        updateAllVariationsOverset,
    };
};
